<script setup lang="ts">
import { Close } from '@element-plus/icons-vue'
import { useTabsViewStore } from '@/store/modules/tabs'
import { nextTick } from 'vue'
import type { RouteLocationNormalized, RouteMeta } from 'vue-router'
import { ElScrollbar } from 'element-plus'
import { getRawRoute } from '@/utils'
import type { Tab } from '@/store/modules/tabs'
import { PAY_CENTER_PATH } from '@/utils/micro-app'

const route = useRoute()
const router = useRouter()
const tabsViewStore = useTabsViewStore()
const activeKey = ref<string>(route.name as string)

function getTagKey(tag: RouteLocationNormalized) {
  return tag.fullPath || tag.path
}

function isActive(tag: RouteLocationNormalized): boolean {
  return getTagKey(tag) === activeKey.value
}

const closable = computed(() => {
  return unref(getTabs).length > 1
})

function handleChangeTab(tag: Tab) {
  const visitedRecords = tag.visitedRecords
  const lastRecord = visitedRecords[visitedRecords.length - 1]
  // const currentActivePath = visitedRecords.at(-1)?.fullPath ?? tag.fullPath ?? '/'
  const currentActivePath = lastRecord?.fullPath ?? tag.fullPath ?? '/'
  router.push(currentActivePath)
}

function getTabName({ meta: { title, menuName } }: RouteLocationNormalized) {
  return menuName ?? title ?? ''
}

const scrollbarRef = ref<InstanceType<typeof ElScrollbar>>()
const tabsWrapperRef = ref<HTMLDivElement>()
async function scrollToActive() {
  await nextTick()
  if (!scrollbarRef.value || !tabsWrapperRef.value) return
  const currentTab = tabsWrapperRef.value.querySelector('.active') as any
  if (!currentTab || typeof currentTab.offsetLeft !== 'number') return
  scrollbarRef.value.scrollTo(currentTab.offsetLeft, 0)
}

watch(
  route,
  (newRoute) => {
    const { path, fullPath, meta = {} } = newRoute
    const { parentMenu, hideInTab } = meta as RouteMeta
    const isHide = !hideInTab ? null : parentMenu
    const p = isHide || fullPath || path
    if (unref(activeKey) !== p) {
      activeKey.value = p as string
    }
    let res = newRoute
    if (newRoute.path === PAY_CENTER_PATH) {
      // 结算系统内嵌页
      const { hash, fullPath, matched, ...rest } = newRoute
      const hashWithoutQuery = hash.split('?')[0]
      const childRoute = matched[matched.length - 1]?.children?.find(
        (item) => item.path === hashWithoutQuery
      ) as any
      if (childRoute) {
        res = {
          ...rest,
          fullPath,
          ...childRoute,
          path: fullPath,
          matched: [childRoute]
        }
      }
    }
    tabsViewStore.addTab(getRawRoute(res))
    scrollToActive()
  },
  {
    immediate: true
  }
)

const getTabs = computed(() => tabsViewStore.getTabs.filter((item) => !item.meta?.hideInTab))

function handleRemoveTab(tag: Tab) {
  if (unref(closable)) {
    tabsViewStore.removeTab(tag)
  }
}
</script>

<template>
  <ElScrollbar ref="scrollbarRef">
    <div ref="tabsWrapperRef" class="h-[68px] flex justify-center">
      <div class="tabs-wrapper">
        <div
          v-for="tag in getTabs"
          :key="getTagKey(tag)"
          :class="isActive(tag) ? 'active' : 'inactive'"
          class="cursor-pointer tabs shrink-0"
          @click="handleChangeTab(tag)"
        >
          <span>{{ getTabName(tag) }}</span>
          <ElIcon
            v-if="closable"
            class="icon"
            style="margin-left: 2px"
            @click.prevent.stop="handleRemoveTab(tag)"
          >
            <Close />
          </ElIcon>
        </div>
      </div>
    </div>
  </ElScrollbar>
</template>

<style scoped lang="scss">
$color: #1761f1;

.tabs-wrapper {
  display: flex;
  align-items: center;

  .tabs {
    display: flex;
    align-items: center;
    gap: 0 8px;
    color: #000;
    border-radius: 4px;
    height: 28px;
    background: #fff;
    line-height: 28px;
    padding: 0 16px;
    font-size: 12px;

    &.active {
      background: $color;
      color: #fff;
    }

    &.inactive {
      background-color: #f0f0f0;
    }

    .icon {
      &:hover {
        color: $color;
        background: #e8f3ff;
        border-radius: 50%;
      }
    }

    &:not(:last-child) {
      margin-right: 12px;
    }
  }
}
</style>
